import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { ArrowLeft, ArrowRight, HeartPulse, ShieldAlert } from "lucide-react";

const STEPS = [
  { id: "personal", title: "Dati personali", subtitle: "Informazioni di base", fields: [
    { key: "first_name", label: "Nome", type: "text", required: true },
    { key: "last_name", label: "Cognome", type: "text", required: true },
    { key: "birth_date", label: "Data di nascita", type: "date", required: true },
    { key: "sex", label: "Sesso", type: "chips", required: true, options: [{ v: "female", label: "Femmina" }, { v: "male", label: "Maschio" }] },
    { key: "height_cm", label: "Altezza (cm)", type: "number", required: true },
    { key: "weight_kg", label: "Peso attuale (kg)", type: "number", required: true },
    { key: "work_activity", label: "Attività lavorativa", type: "chips", options: ["Sedentaria", "Moderatamente attiva", "Molto attiva", "Lavoro fisicamente impegnativo"] },
    { key: "daily_steps", label: "Quanti passi fai mediamente al giorno?", type: "chips", options: ["<3.000", "3.000–5.000", "5.000–8.000", "8.000–10.000", "10.000+", "Non lo so"] },
  ]},
  { id: "goal", title: "Il tuo obiettivo", fields: [
    { key: "objectives", label: "Qual è il tuo obiettivo principale?", type: "multi", required: true, options: ["Dimagrimento", "Ricomposizione corporea", "Aumento massa muscolare", "Glutei", "Forza", "Tonificazione", "Miglioramento della forma fisica", "Performance", "Benessere generale", "Altro"] },
    { key: "results_text", label: "Quali risultati vorresti ottenere?", type: "textarea" },
    { key: "motivation", label: "Quanto sei motivata/o a raggiungere il tuo obiettivo?", type: "scale5" },
    { key: "timeframe", label: "Entro quanto tempo vorresti iniziare a vedere i primi cambiamenti?", type: "chips", options: ["30 giorni", "2–3 mesi", "3–6 mesi", "6+ mesi"], hint: "Questa domanda serve per capire le aspettative, non per promettere risultati." },
  ]},
  { id: "experience", title: "La tua esperienza", fields: [
    { key: "trained_before", label: "Hai già praticato attività fisica?", type: "chips", required: true, options: ["Mai", "Da principiante", "Regolarmente", "Mi alleno già da diversi anni"] },
    { key: "trains_now", label: "Attualmente ti alleni?", type: "chips", options: ["No", "1 volta/settimana", "2 volte", "3 volte", "4 volte", "5+ volte"] },
    { key: "current_activities", label: "Quali attività pratichi attualmente?", type: "text" },
    { key: "training_places", label: "Dove ti allenerai?", type: "multi", options: ["Casa", "Palestra", "Outdoor", "Online / attrezzatura variabile"] },
  ]},
  { id: "safety", title: "Prima di iniziare", subtitle: "La tua sicurezza viene prima del programma.", fields: [
    { key: "has_condition", label: "Hai attualmente una condizione medica, un problema fisico o un sintomo che potrebbe influenzare la tua capacità di allenarti?", type: "yesno", required: true },
    { key: "condition_details", label: "Quale?", type: "textarea", showIf: (a) => a.has_condition === "yes" },
    { key: "chest_pain", label: "Hai mai avuto dolore al petto durante l'attività fisica?", type: "yesno", required: true },
    { key: "fainting", label: "Hai avuto episodi di perdita di coscienza, svenimento o forte capogiro durante l'attività fisica?", type: "yesno", required: true },
    { key: "cardio_limit", label: "Ti è mai stato diagnosticato un problema cardiovascolare per il quale ti è stato consigliato di limitare l'attività fisica?", type: "yesno", required: true },
    { key: "medical_avoid", label: "Hai una condizione medica per la quale ti è stato consigliato di evitare o modificare l'esercizio fisico?", type: "yesno", required: true },
    { key: "injury_current", label: "Hai attualmente un infortunio, dolore o limitazione del movimento?", type: "yesno", required: true },
  ]},
  { id: "pain", title: "Dolori e limitazioni", showIf: (a) => a.injury_current === "yes", fields: [
    { key: "pain_areas", label: "Dove hai dolore o limitazioni?", type: "multi", options: ["Cervicale", "Spalla", "Gomito", "Polso", "Schiena", "Anca", "Ginocchio", "Caviglia", "Piede", "Altro"] },
    { key: "pain_intensity", label: "Intensità del dolore", type: "scale010" },
    { key: "pain_duration", label: "Da quanto tempo?", type: "chips", options: ["Da pochi giorni", "Da settimane", "Da mesi", "Da anni", "Ricorrente"] },
    { key: "pain_worse", label: "Quali movimenti o attività lo peggiorano?", type: "textarea" },
    { key: "pain_diagnosis", label: "Hai ricevuto una diagnosi o indicazioni da un medico/fisioterapista?", type: "yesno" },
    { key: "pain_indications", label: "Se sì, quali indicazioni devi rispettare durante l'attività fisica?", type: "textarea", showIf: (a) => a.pain_diagnosis === "yes" },
  ]},
  { id: "conditions", title: "Patologie e interventi", fields: [
    { key: "diagnosed", label: "Ti è mai stata diagnosticata una condizione medica rilevante per l'attività fisica?", type: "yesno", required: true },
    { key: "diagnosed_areas", label: "Seleziona le aree interessate", type: "multi", options: ["Cardiovascolare", "Respiratoria", "Metabolica", "Muscolo-scheletrica", "Neurologica", "Ortopedica", "Altro"], showIf: (a) => a.diagnosed === "yes" },
    { key: "surgeries", label: "Hai subito interventi chirurgici?", type: "yesno" },
    { key: "surgeries_details", label: "Se sì: quale e quando?", type: "text", showIf: (a) => a.surgeries === "yes" },
  ]},
  { id: "meds", title: "Farmaci e trattamenti", fields: [
    { key: "medications_flag", label: "Assumi attualmente farmaci o segui trattamenti che potrebbero influenzare allenamento, peso, energia, frequenza cardiaca o recupero?", type: "yesno", required: true },
    { key: "med_name", label: "Nome del farmaco/trattamento", type: "text", showIf: (a) => a.medications_flag === "yes" },
    { key: "med_since", label: "Da quanto tempo lo utilizzi?", type: "text", showIf: (a) => a.medications_flag === "yes" },
    { key: "med_indications", label: "Indicazioni o limitazioni del medico da conoscere per il percorso di allenamento", type: "textarea", showIf: (a) => a.medications_flag === "yes" },
  ]},
  { id: "female", title: "Il tuo profilo femminile", subtitle: "Sezione opzionale: rispondi solo a ciò che senti utile condividere.", showIf: (a) => a.sex === "female", fields: [
    { key: "female_status", label: "Quale situazione descrive meglio il tuo momento attuale?", type: "chips", options: ["Ciclo mestruale", "Perimenopausa", "Menopausa", "Post-menopausa", "Contraccezione ormonale", "Altro", "Preferisco non rispondere"] },
    { key: "cycle_regular", label: "Il ciclo è regolare?", type: "chips", options: ["Sì", "No", "Non applicabile"] },
    { key: "female_symptoms", label: "Sintomi che percepisci come rilevanti per il tuo allenamento?", type: "multi", options: ["Nessuno", "Disturbi del sonno", "Vampate", "Affaticamento", "Variazioni dell'energia", "Ritenzione", "Cambiamenti nella composizione corporea", "Altro"] },
    { key: "female_notes", label: "Note (opzionale)", type: "textarea" },
  ]},
  { id: "recovery", title: "Recupero", subtitle: "Quanto recuperi?", fields: [
    { key: "sleep_hours", label: "Quante ore dormi mediamente?", type: "chips", options: ["<5", "5–6", "6–7", "7–8", "8+"] },
    { key: "sleep_quality", label: "Come valuti la qualità del tuo sonno?", type: "scale5" },
    { key: "stress", label: "Quanto stress percepisci nella tua vita quotidiana?", type: "scale5" },
    { key: "energy", label: "Come valuti il tuo livello di energia durante la giornata?", type: "scale5" },
    { key: "realistic_days", label: "Quanti giorni alla settimana riesci realisticamente ad allenarti?", type: "chips", options: ["1", "2", "3", "4", "5+"] },
  ]},
  { id: "availability", title: "Disponibilità", subtitle: "Fondamentale per costruire il tuo programma.", fields: [
    { key: "session_length", label: "Quanto tempo puoi dedicare a ogni allenamento?", type: "chips", options: ["20–30 min", "30–45 min", "45–60 min", "60–90 min", "90+ min"] },
    { key: "equipment_list", label: "Quale attrezzatura hai a disposizione?", type: "multi", options: ["Nessuna", "Elastici", "Manubri", "Kettlebell", "Bilanciere", "Panca", "Macchine", "Palestra completa", "Altro"] },
  ]},
  { id: "nutrition", title: "Alimentazione", subtitle: "Food & Lifestyle Check.", fields: [
    { key: "diet_description", label: "Come descriveresti attualmente la tua alimentazione?", type: "chips", options: ["Regolare", "Poco regolare", "Molto variabile", "Mangio spesso fuori casa", "Non so come organizzarmi"] },
    { key: "food_allergies", label: "Hai allergie o intolleranze alimentari conosciute?", type: "yesno" },
    { key: "food_allergies_details", label: "Se sì, quali?", type: "text", showIf: (a) => a.food_allergies === "yes" },
    { key: "diet_regime", label: "Segui attualmente un regime alimentare particolare?", type: "chips", options: ["Nessuno", "Vegetariano", "Vegano", "Senza glutine", "Low carb", "Altro"] },
    { key: "foods_excluded", label: "Ci sono alimenti che non consumi per scelta personale?", type: "text" },
  ]},
  { id: "lifestyle", title: "Lifestyle", fields: [
    { key: "alcohol", label: "Quanto spesso consumi alcol?", type: "chips", options: ["Mai", "Raramente", "1–2 volte/settimana", "3+ volte/settimana"] },
    { key: "smoking", label: "Fumi?", type: "yesno" },
    { key: "meals_per_day", label: "Quanti pasti consumi mediamente al giorno?", type: "number" },
    { key: "water", label: "Quanta acqua bevi mediamente?", type: "chips", options: ["<1 L", "1–1,5 L", "1,5–2 L", "2+ L"] },
  ]},
  { id: "declaration", title: "Dichiarazione", declaration: true, fields: [] },
];

function Field({ f, value, onChange }) {
  const tid = `hc-field-${f.key}`;
  if (f.type === "chips" || f.type === "yesno") {
    const opts = f.type === "yesno" ? [{ v: "no", label: "No" }, { v: "yes", label: "Sì" }] : f.options.map((o) => typeof o === "string" ? { v: o, label: o } : o);
    return (
      <div className="flex flex-wrap gap-2" data-testid={tid}>
        {opts.map((o) => (
          <button type="button" key={o.v} data-testid={`${tid}-${o.v}`} onClick={() => onChange(o.v)}
            className={`px-4 py-2.5 rounded-full text-sm font-bold border-2 transition-colors ${
              value === o.v ? "bg-primary border-primary text-primary-foreground" : "border-border hover:border-primary/60"}`}>
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  if (f.type === "multi") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (v) => onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
    return (
      <div className="flex flex-wrap gap-2" data-testid={tid}>
        {f.options.map((o) => (
          <button type="button" key={o} data-testid={`${tid}-${o}`} onClick={() => toggle(o)}
            className={`px-4 py-2.5 rounded-full text-sm font-bold border-2 transition-colors ${
              arr.includes(o) ? "bg-primary border-primary text-primary-foreground" : "border-border hover:border-primary/60"}`}>
            {o}
          </button>
        ))}
      </div>
    );
  }
  if (f.type === "scale5" || f.type === "scale010") {
    const start = f.type === "scale010" ? 0 : 1;
    const end = f.type === "scale010" ? 10 : 5;
    return (
      <div className="flex flex-wrap gap-2" data-testid={tid}>
        {Array.from({ length: end - start + 1 }, (_, i) => start + i).map((n) => (
          <button type="button" key={n} data-testid={`${tid}-${n}`} onClick={() => onChange(n)}
            className={`w-11 h-11 rounded-xl text-sm font-black border-2 transition-colors ${
              value === n ? "bg-primary border-primary text-primary-foreground" : "border-border hover:border-primary/60"}`}>
            {n}
          </button>
        ))}
      </div>
    );
  }
  if (f.type === "textarea") {
    return <textarea data-testid={tid} rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} className="kc-input" />;
  }
  return <input data-testid={tid} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
    min={f.type === "number" ? "0" : undefined} step={f.type === "number" ? "0.1" : undefined}
    value={value || ""} onChange={(e) => onChange(e.target.value)} className="kc-input" />;
}

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(-1);
  const [answers, setAnswers] = useState(() => {
    const parts = (user?.name || "").trim().split(/\s+/);
    return { first_name: parts[0] || "", last_name: parts.slice(1).join(" ") || "" };
  });
  const [decl, setDecl] = useState({ truth: false, medical: false });
  const [saving, setSaving] = useState(false);
  const [doneStatus, setDoneStatus] = useState(null);

  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "client" || user.onboarding_completed) return <Navigate to="/app" replace />;

  const steps = STEPS.filter((s) => !s.showIf || s.showIf(answers));
  const step = steps[Math.min(Math.max(stepIdx, 0), steps.length - 1)];
  const setA = (k) => (v) => setAnswers((a) => ({ ...a, [k]: v }));

  const stepValid = () => {
    if (step.declaration) return decl.truth && decl.medical;
    return step.fields.every((f) => {
      if (!f.required || (f.showIf && !f.showIf(answers))) return true;
      const v = answers[f.key];
      if (f.type === "multi") return Array.isArray(v) && v.length > 0;
      return v !== undefined && v !== null && String(v).trim() !== "";
    });
  };

  const submit = async () => {
    setSaving(true);
    try {
      const num = (v) => v ? Number(String(v).replace(",", ".")) : null;
      const payload = {
        name: [answers.first_name, answers.last_name].filter(Boolean).join(" ").trim(),
        birth_date: answers.birth_date || "", sex: answers.sex || "",
        weight_kg: num(answers.weight_kg), height_cm: num(answers.height_cm),
        goals: [...(answers.objectives || []), answers.results_text].filter(Boolean).join(" — "),
        experience: answers.trained_before || "",
        availability: answers.realistic_days ? parseInt(answers.realistic_days) : null,
        equipment: (answers.equipment_list || []).join(", "),
        allergies: answers.food_allergies === "yes" ? (answers.food_allergies_details || "Sì") : "",
        injuries: answers.injury_current === "yes"
          ? [`Aree: ${(answers.pain_areas || []).join(", ")}`, answers.pain_intensity != null ? `intensità ${answers.pain_intensity}/10` : "", answers.pain_duration, answers.pain_worse ? `Peggiora con: ${answers.pain_worse}` : "", answers.pain_indications ? `Indicazioni: ${answers.pain_indications}` : ""].filter(Boolean).join(". ")
          : "",
        medications: answers.medications_flag === "yes"
          ? [answers.med_name, answers.med_since ? `da ${answers.med_since}` : "", answers.med_indications].filter(Boolean).join(" — ")
          : "",
        health_check: answers,
      };
      const { data } = await api.post("/client/onboarding", payload);
      setUser({ ...user, onboarding_completed: true, health_status: data.health_status });
      if (data.health_status === "red") setDoneStatus("red");
      else navigate("/app", { replace: true });
    } catch (err) {
      alert(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (doneStatus === "red") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="hc-red-banner">
        <div className="relative w-full max-w-lg kc-card p-8 text-center">
          <span className="w-14 h-14 rounded-full bg-destructive/15 grid place-items-center mb-4 mx-auto">
            <ShieldAlert className="w-7 h-7 text-destructive" />
          </span>
          <h1 className="text-2xl font-black uppercase tracking-tight mb-3">La tua sicurezza prima di tutto</h1>
          <p className="text-muted-foreground text-sm mb-6">
            Prima di iniziare o modificare il programma di allenamento, potrebbe essere opportuno acquisire
            indicazioni da un professionista sanitario. Il tuo coach è stato avvisato e ti contatterà.
          </p>
          <button data-testid="hc-red-continue" onClick={() => navigate("/app", { replace: true })} className="kc-btn-primary w-full">
            Ho capito, vai all'app
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative" data-testid="onboarding-page">
      <img src="https://images.unsplash.com/photo-1641337221253-fdc7237f6b61?crop=entropy&cs=srgb&fm=jpg&q=85"
        alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/40" />
      <div className="relative w-full max-w-lg kc-card p-6 sm:p-8 max-h-[92vh] overflow-y-auto">
        {stepIdx < 0 ? (
          <div data-testid="hc-intro">
            <span className="w-12 h-12 rounded-full bg-primary/12 grid place-items-center mb-4">
              <HeartPulse className="w-6 h-6 text-primary" />
            </span>
            <p className="kc-eyebrow mb-1">Health &amp; Fitness Check</p>
            <h1 className="text-3xl font-black uppercase tracking-tight mb-2">Il tuo punto di partenza</h1>
            <p className="text-muted-foreground text-sm mb-6">
              Raccogliamo le informazioni necessarie per personalizzare il percorso e identificare eventuali
              situazioni che richiedono attenzione. Ci vogliono circa 4 minuti.
            </p>
            <button data-testid="hc-start" onClick={() => setStepIdx(0)}
              className="kc-btn-primary w-full inline-flex items-center justify-center gap-2">
              Inizia <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div data-testid={`hc-step-${step.id}`}>
            <div className="mb-5">
              <div className="flex justify-between text-xs font-bold text-muted-foreground mb-1.5">
                <span>Passo {stepIdx + 1} di {steps.length}</span>
                <span data-testid="hc-progress">{Math.round(((stepIdx + 1) / steps.length) * 100)}%</span>
              </div>
              <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${((stepIdx + 1) / steps.length) * 100}%` }} />
              </div>
            </div>
            <h2 className="text-2xl font-black uppercase tracking-tight mb-1">{step.title}</h2>
            {step.subtitle && <p className="text-muted-foreground text-sm mb-4">{step.subtitle}</p>}
            <div className="space-y-5 mt-4">
              {step.fields.filter((f) => !f.showIf || f.showIf(answers)).map((f) => (
                <div key={f.key}>
                  <label className="block text-sm font-bold mb-2">{f.label}{f.required && <span className="text-primary"> *</span>}</label>
                  <Field f={f} value={answers[f.key]} onChange={setA(f.key)} />
                  {f.hint && <p className="text-xs text-muted-foreground mt-1.5 italic">{f.hint}</p>}
                </div>
              ))}
              {step.declaration && (
                <div className="space-y-3">
                  {[
                    ["truth", "Dichiaro di aver fornito informazioni corrette e aggiornate sul mio stato di salute e sulle mie condizioni fisiche. Mi impegno a comunicare eventuali cambiamenti rilevanti durante il percorso."],
                    ["medical", "Sono consapevole che il programma di allenamento non sostituisce la valutazione, la diagnosi o il trattamento di un medico o di altro professionista sanitario."],
                  ].map(([k, text]) => (
                    <label key={k} data-testid={`hc-decl-${k}`} className="flex items-start gap-3 kc-card p-4 cursor-pointer">
                      <input type="checkbox" checked={decl[k]} onChange={(e) => setDecl({ ...decl, [k]: e.target.checked })}
                        className="mt-0.5 w-5 h-5 accent-[#9C2745] shrink-0" />
                      <span className="text-sm text-muted-foreground">{text}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-7">
              <button data-testid="hc-back" onClick={() => setStepIdx(stepIdx - 1)}
                className="kc-btn-ghost !px-4 inline-flex items-center gap-1.5">
                <ArrowLeft className="w-4 h-4" /> Indietro
              </button>
              {step.declaration ? (
                <button data-testid="hc-submit" onClick={submit} disabled={saving || !stepValid()}
                  className="kc-btn-primary flex-1 inline-flex items-center justify-center gap-2">
                  {saving ? "Salvataggio..." : "Completa Health Check"}
                </button>
              ) : (
                <button data-testid="hc-next" onClick={() => stepValid() && setStepIdx(stepIdx + 1)} disabled={!stepValid()}
                  className="kc-btn-primary flex-1 inline-flex items-center justify-center gap-2">
                  Avanti <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
